'use client'

import * as React from 'react'
import { Decimal } from 'decimal.js'
import { ChevronDown, ChevronsUpDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  OrderFieldDivider,
  OrderFieldStepper,
} from '@/features/order/order-form/components/widget/order-field-stepper'
import { useAdvancePresetTourOnQuantity } from '@/hooks/use-advance-preset-tour-on-quantity'
import { useFocusOnMount } from '@/hooks/use-focus-on-mount'
import { useNumberInput } from '@/hooks/use-number-format'
import { cn, safeParseFloat } from '@/lib/utils'
import { PRESET_TOUR_TARGET } from '@/state/preset-tour'

export interface TickerInfo {
  label: string // e.g., "BTC", "USD", "ETH"
  priceInUSD: number // price in USD for conversion
}

interface InputWithTickerListProps {
  value: string
  onValueChange: (value: string) => void
  tickers: TickerInfo[]
  selectedTicker: string
  onTickerChange: (ticker: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  // Shows the value but refuses edits (typing and the stepper); `title` explains why.
  readOnly?: boolean
  title?: string
  denominationMap?: Record<string, string>
  onDenominationMapChange?: (map: Record<string, string>) => void
  autoFocus?: boolean
  showNumberPad?: boolean
  step?: number
  amountInputIndex?: number
  inputRef?: React.Ref<HTMLInputElement>
}

// Calculate step dynamically based on price magnitude
const getStep = (priceInUSD: number) => {
  if (priceInUSD <= 0) return 1
  const magnitude = Math.floor(Math.log10(priceInUSD))
  return Math.pow(10, -magnitude)
}

export function InputWithTickerList({
  value,
  onValueChange,
  tickers,
  selectedTicker,
  onTickerChange,
  placeholder = '0',
  className,
  disabled = false,
  readOnly = false,
  title,
  denominationMap,
  onDenominationMapChange,
  autoFocus = false,
  showNumberPad = false,
  step: stepProp,
  amountInputIndex,
  inputRef,
}: InputWithTickerListProps) {
  const focusOnMountRef = useFocusOnMount<HTMLInputElement>(autoFocus, inputRef)
  useAdvancePresetTourOnQuantity(value)

  // Track which ticker the user last directly input a value for
  const lastInputTickerRef = React.useRef<string | null>(null)

  // Calculate dynamic step based on selected ticker's USD price
  const selectedTickerInfo = tickers.find((t) => t.label === selectedTicker)
  const step = stepProp ?? getStep(selectedTickerInfo?.priceInUSD ?? 1)
  const currentValue = safeParseFloat(value) || 0
  const numberInput = useNumberInput(value)
  const isMinStep = currentValue <= step

  const applyValueChange = (formattedValue: string) => {
    onValueChange(formattedValue)

    // Mark this ticker as the last one user directly input
    lastInputTickerRef.current = selectedTicker

    // Update denomination map if provided
    if (denominationMap && onDenominationMapChange) {
      const newMap = { ...denominationMap, [selectedTicker]: formattedValue }
      onDenominationMapChange(newMap)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Typed in the user's separator style; refused keystrokes (ambiguous marks) come back null.
    const val = numberInput.parse(e.target.value)
    if (val !== null) {
      onValueChange(val)

      // Mark this ticker as the last one user directly input
      lastInputTickerRef.current = selectedTicker

      // Update denomination map if provided
      if (denominationMap && onDenominationMapChange) {
        const newMap = { ...denominationMap, [selectedTicker]: val }
        onDenominationMapChange(newMap)
      }
    }
  }

  const handleIncrement = () => {
    const newValue = new Decimal(currentValue).plus(new Decimal(step))
    applyValueChange(newValue.toFixed())
  }

  const handleDecrement = () => {
    if (isMinStep) return
    const newValue = Decimal.max(
      new Decimal(step),
      new Decimal(currentValue).minus(new Decimal(step))
    )
    applyValueChange(newValue.toFixed())
  }

  const handleTickerChange = (newTicker: string) => {
    if (newTicker === selectedTicker) return

    // A value that arrived from outside (a position-table add, a preset) was never typed, so
    // nothing marked it as the original: every switch re-converted it at the live price and
    // ETH → USD → ETH drifted (110 → 109.6). Adopt the current value as the original the
    // first time the unit switches, so switching back restores it exactly.
    let map = denominationMap
    if (lastInputTickerRef.current === null && value) {
      lastInputTickerRef.current = selectedTicker
      if (map) map = { ...map, [selectedTicker]: value }
    }

    // Check if we should restore the original user input for this ticker
    // Only restore if:
    // 1. Switching back to the ticker where user last directly input (lastInputTickerRef)
    // 2. That ticker has a stored value in denominationMap
    // 3. Current ticker is NOT the last input ticker (meaning current value is converted, not user input)
    const isReturningToLastInputTicker = newTicker === lastInputTickerRef.current
    const hasStoredValueForTarget = map?.[newTicker]
    const currentTickerIsNotLastInput = selectedTicker !== lastInputTickerRef.current
    // …and the shown value is still the one this switch produced: an amount set from outside
    // since then (an orderbook click) must be converted, not swapped for the stale original.
    // (Compared without grouping commas: the form stores "3,005.12", the map "3005.12".)
    const plain = (v: string | undefined) => v?.replace(/,/g, '')
    const valueUnchangedSinceSwitch = plain(map?.[selectedTicker]) === plain(value)

    if (
      isReturningToLastInputTicker &&
      hasStoredValueForTarget &&
      currentTickerIsNotLastInput &&
      valueUnchangedSinceSwitch
    ) {
      // Restore the original user input value
      onValueChange(map![newTicker])
      onTickerChange(newTicker)
      return
    }

    const currentTickerInfo = tickers.find((t) => t.label === selectedTicker)
    const newTickerInfo = tickers.find((t) => t.label === newTicker)

    // Convert current value to USD first, then to new ticker
    const currentValue = safeParseFloat(value) || 0
    if (currentValue === 0) {
      if (map !== denominationMap) onDenominationMapChange?.(map!)
      onTickerChange(newTicker)
      return
    }

    // No price to convert with (the leg's ticker hasn't arrived): the number can't carry over,
    // and keeping it under the new unit silently changes the order — "10" USD became "10" BTC
    // and the preview read $837k. Switch the unit and clear the amount instead.
    if (
      !currentTickerInfo ||
      !newTickerInfo ||
      !(currentTickerInfo.priceInUSD > 0) ||
      !(newTickerInfo.priceInUSD > 0)
    ) {
      onValueChange('')
      onTickerChange(newTicker)
      if (map && onDenominationMapChange) onDenominationMapChange({ ...map, [newTicker]: '' })
      return
    }

    // Convert: current value -> USD -> new ticker
    const valueInUSD = currentValue * currentTickerInfo.priceInUSD
    const convertedValue = valueInUSD / newTickerInfo.priceInUSD

    // Use 5 decimal places as requested by user to avoid precision issues
    const formattedValue = safeParseFloat(convertedValue.toFixed(5)).toString()

    onValueChange(formattedValue)
    onTickerChange(newTicker)

    // Update denomination map to store this converted value
    // This allows us to restore it if user switches back without editing
    if (map && onDenominationMapChange) {
      onDenominationMapChange({ ...map, [newTicker]: formattedValue })
    }
  }

  // Quick mode: if exactly 2 tickers, toggle between them on click
  const isQuickMode = tickers.length === 2
  const handleQuickToggle = () => {
    if (!isQuickMode) return
    const otherTicker = tickers.find((t) => t.label !== selectedTicker)
    if (otherTicker) {
      handleTickerChange(otherTicker.label)
    }
  }

  // Same shape as the limit price field: [amount | unit | +/−]. A container, so a narrow
  // field (the order form at its smallest) slims the unit box instead of squeezing the number.
  const unitClass =
    'flex h-full min-w-18.5 shrink-0 items-center justify-center gap-1 px-3 text-sm font-medium text-foreground/60 transition-colors hover:bg-foreground/5 hover:text-foreground @max-[11rem]:min-w-0 @max-[11rem]:px-1.5 @max-[11rem]:text-xs'
  return (
    <div
      title={title}
      className={cn(
        '@container flex h-9 items-center overflow-hidden bg-transparent transition-colors focus-within:border-white/20',
        disabled && 'opacity-50',
        className
      )}
    >
      <input
        ref={focusOnMountRef}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        data-1p-ignore
        data-preset-tour-target={PRESET_TOUR_TARGET.quantity}
        data-order-amount-index={amountInputIndex}
        value={numberInput.display}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className="text-foreground placeholder:text-foreground/20 h-full min-w-0 flex-1 bg-transparent px-3 text-right text-sm font-medium outline-none"
      />
      <OrderFieldDivider />
      {isQuickMode ? (
        <button
          type="button"
          onClick={handleQuickToggle}
          disabled={disabled || readOnly}
          tabIndex={-1}
          aria-label={`${selectedTicker}, switch unit`}
          className={cn(
            unitClass,
            'transition-[color,background-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] not-disabled:cursor-pointer active:scale-[0.97] motion-reduce:transition-none',
            (disabled || readOnly) && 'pointer-events-none'
          )}
        >
          {selectedTicker}
          <ChevronsUpDown className="text-foreground/40 size-3 shrink-0" />
        </button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={disabled || readOnly || tickers.length <= 1}
            tabIndex={-1}
            className={cn(
              'group',
              unitClass,
              (disabled || readOnly || tickers.length <= 1) && 'pointer-events-none'
            )}
          >
            {selectedTicker}
            <ChevronDown className="size-3 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="z-[60] -mt-1.5 min-w-18.5 overflow-hidden p-0"
          >
            {tickers
              .filter((ticker) => ticker.label !== selectedTicker)
              .map((ticker) => (
                <DropdownMenuItem
                  key={ticker.label}
                  onClick={() => handleTickerChange(ticker.label)}
                  className="hover:bg-foreground/10 focus:bg-foreground/10 text-foreground/80 hover:text-foreground cursor-pointer text-sm font-medium transition-colors"
                >
                  {ticker.label}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {showNumberPad && (
        <>
          <OrderFieldDivider />
          <OrderFieldStepper
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            disabled={disabled || readOnly}
            decrementDisabled={isMinStep}
          />
        </>
      )}
    </div>
  )
}
